import { api, apiErrorFromResponse, ApiError } from '../../lib/api';
import { firebaseAuth } from '../../lib/firebase';
import type { InspirationAdaptFor, InspirationReach, InspirationUsage, PortalInspirationEntry, StaffInspirationRow } from '../../types/portalInspiration';
import type { InspirationAiField } from '../../types/portalInspiration';
import type { PortalAudienceRule } from '../../types/portalLibrary';
import type { PortalAssetUploadSkip } from '../../types/portalAsset';
import type { FrameSource } from '../../lib/portalInspiration/draft';

// Portal inspiration — the STAFF API. ⚠️ There is no client-facing call in this file: the
// portal's own read lives in src/modules/clientPortal/portal/inspiration/.

const BASE = '/api/portal-inspiration';
const json = (body: unknown) => ({ method: 'POST', body: JSON.stringify(body) });

export async function listInspiration(): Promise<StaffInspirationRow[]> {
  return (await api<{ rows: StaffInspirationRow[] }>(BASE)).rows;
}

export async function listInspirationTags(): Promise<string[]> {
  return (await api<{ tags: string[] }>(`${BASE}/tags`)).tags;
}

export async function getKyckConsent(): Promise<{ version: string; text: string }> {
  return api(`${BASE}/consent`);
}

export async function previewInspirationReach(
  audience: PortalAudienceRule[],
): Promise<{ reach: InspirationReach; refusal: string | null }> {
  return api(`${BASE}/reach`, json({ audience }));
}

export type UploadDefaults = {
  audience: PortalAudienceRule[];
  usage: InspirationUsage | null;
  tags: string[];
  /** Absent = derived from the audience. */
  adaptFor?: InspirationAdaptFor;
};

export type InspirationUploadTicket = {
  entryId: string | null;
  assetId: string;
  filename: string;
  mediaType: 'image' | 'video';
  uploadUrl: string;
  requiredHeaders: Record<string, string>;
};

export type InspirationUploadResponse = {
  items: InspirationUploadTicket[];
  skipped: PortalAssetUploadSkip[];
  duplicateCheckRan: boolean;
  entryFailures: Array<{ assetId: string; filename: string }>;
};

export async function createInspirationUploads(input: {
  consentWordingVersion: string;
  files: Array<{ filename: string; contentType: string; sizeBytes: number }>;
  defaults: UploadDefaults;
}): Promise<InspirationUploadResponse> {
  return api(`${BASE}/uploads`, json(input));
}

export async function completeInspirationUpload(entryId: string): Promise<{ lifecycle: string }> {
  return api(`${BASE}/${encodeURIComponent(entryId)}/complete`, { method: 'POST' });
}

export type InspirationPatchBody = Partial<{
  title: string;
  trendLine: string | null;
  suggestedLines: string[];
  adaptFor: InspirationAdaptFor;
  description: string;
  howTo: string;
  usage: InspirationUsage | null;
  tags: string[];
  audience: PortalAudienceRule[];
  visibleFromMs: number;
  visibleUntilMs: number | null;
}>;

export async function updateInspiration(id: string, patch: InspirationPatchBody): Promise<PortalInspirationEntry> {
  return (await api<{ entry: PortalInspirationEntry }>(`${BASE}/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) })).entry;
}

export async function publishInspiration(id: string): Promise<PortalInspirationEntry> {
  return (await api<{ entry: PortalInspirationEntry }>(`${BASE}/${encodeURIComponent(id)}/publish`, { method: 'POST' })).entry;
}

export async function unpublishInspiration(id: string): Promise<PortalInspirationEntry> {
  return (await api<{ entry: PortalInspirationEntry }>(`${BASE}/${encodeURIComponent(id)}/unpublish`, { method: 'POST' })).entry;
}

export async function deleteInspiration(id: string): Promise<void> {
  await api(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export type BulkResult = { id: string; ok: boolean; error?: string };

export async function bulkInspiration(op: 'publish' | 'unpublish' | 'delete', ids: string[]): Promise<BulkResult[]> {
  return (await api<{ results: BulkResult[] }>(`${BASE}/bulk`, json({ op, ids }))).results;
}

export type DraftFramesBody = {
  frames: Array<{ base64: string; timeSec: number }>;
  durationSec: number | null;
  source: FrameSource;
  note: string | null;
};

export type DraftResponse = {
  entry: PortalInspirationEntry;
  written: InspirationAiField[];
  kept: InspirationAiField[];
  framesSeen: number;
};

/** ⚠️ ON-DEMAND ONLY — called from a button (or the bulk "Draft with AI" loop, which is the same button pressed for several). */
export async function draftInspiration(id: string, body: DraftFramesBody): Promise<DraftResponse> {
  return api(`${BASE}/${encodeURIComponent(id)}/draft`, json(body));
}

/**
 * The 720p preview mp4 of an inspiration video, through the SAME-ORIGIN staff proxy (so a frame capture is not
 * blocked by a CDN with no CORS headers). Fetched with the bearer token (a <video src> cannot send one) and
 * returned as a Blob. ⚠️ ON-DEMAND ONLY - called from the draft capture chain, never on load.
 */
export async function fetchInspirationPreviewVideo(id: string): Promise<Blob> {
  const current = firebaseAuth.currentUser;
  if (!current) throw new ApiError(401, 'Not signed in');
  const res = await fetch(`${BASE}/${encodeURIComponent(id)}/preview-video`, {
    headers: { Authorization: `Bearer ${await current.getIdToken()}` },
  });
  if (!res.ok) throw await apiErrorFromResponse(res);
  return res.blob();
}
